// ============================================================
//  🎬  SURFSTERRE — VIDEO URL REPOSITORY
//  ============================================================
//  This is the ONE file you edit to swap in new video URLs.
//  All videos should be publicly hosted (CDN, YouTube embed,
//  Cloudflare Stream, Vimeo, S3, etc.).
//
//  HOW TO ADD / CHANGE A VIDEO:
//    1. Host your .mp4 somewhere publicly accessible
//    2. Find the section below that matches your use-case
//    3. Replace the placeholder URL string with your real URL
//    4. Save — the game picks it up immediately on next load
//
//  SUPPORTED FORMATS: .mp4 (H.264 recommended for broadest
//  browser support). Keep files under 20 MB for fast loading.
// ============================================================


// ─────────────────────────────────────────────────────────────
//  🎮  TRICK KEY SYSTEM  ← THE ONLY KEYS THAT MATTER
//  ─────────────────────────────────────────────────────────────
//
//  Only TWO keys trigger tricks:
//
//    B  =  Big Air  (jump / boost)
//    N  =  Nosedive (spin / style)
//
//  BASIC TRICKS (single key, while airborne):
//    B pressed once  →  "Big Air"     — 200 pts
//    B pressed twice →  "Double Big"  — 400 pts
//    N pressed once  →  "Nosedive"    — 250 pts
//    N pressed twice →  "Double Nose" — 500 pts
//
//  COMBO TRICKS (B + N together in any order, while airborne):
//    Any sequence mixing B and N = NAMED COMBO (see COMBO_CATALOG below)
//    Named combos award bonus points AND trigger a hype video!
//
// ─────────────────────────────────────────────────────────────


// ─────────────────────────────────────────────────────────────
//  ⚡  COMBO CATALOG
//  ─────────────────────────────────────────────────────────────
//  Each entry defines a named combo, its key sequence,
//  point value, and which video plays when it lands.
//
//  KEY SEQUENCE FORMAT:
//    'B'  = tap B
//    'N'  = tap N
//    Sequences are matched in order within a 3-second window.
//
//  TO ADD A NEW COMBO:
//    1. Pick a sequence of B and N keys (e.g. ['B','N','B'])
//    2. Give it a fun name
//    3. Set a point value (higher = harder / rarer)
//    4. Paste a hosted .mp4 URL in the video field
//    5. Add it to the array below — done!
//
//  POINT GUIDE:
//    500–800   = easy 2-key combo
//    900–1200  = medium 3-key combo
//    1300–1800 = hard 4-key combo
//    2000+     = legendary 5-key combo
// ─────────────────────────────────────────────────────────────

export interface ComboDefinition {
  id: string;           // unique slug — used for logging
  name: string;         // display name shown on screen
  sequence: string[];   // ordered key taps: 'B' or 'N'
  pts: number;          // points awarded
  video: string;        // hosted .mp4 URL (or '' to skip video)
  emoji: string;        // emoji shown in the trick announcer
}

export const COMBO_CATALOG: ComboDefinition[] = [

  // ── 2-KEY COMBOS ──────────────────────────────────────────

  {
    id: 'bn_basic',
    name: 'Surf Snap',
    sequence: ['B', 'N'],
    pts: 600,
    emoji: '🏄',
    video: 'https://gif.ex.plo.re/wp-content/uploads/2026/04/segmind-intro-video-06a96bf8-fd83-46c3-ba6c-6f66ba543679-1.mp4',
    // ↑ Replace with your "Surf Snap" hype video
  },
  {
    id: 'nb_basic',
    name: 'Nose Bomb',
    sequence: ['N', 'B'],
    pts: 650,
    emoji: '💣',
    video: 'https://gif.ex.plo.re/wp-content/uploads/2026/04/segmind-intro-video-bf2ea9e4-3e35-42b8-9c81-3b6bb2d9e696-1.mp4',
    // ↑ Replace with your "Nose Bomb" hype video
  },

  // ── 3-KEY COMBOS ──────────────────────────────────────────

  {
    id: 'bnb',
    name: 'Big Nose Big',
    sequence: ['B', 'N', 'B'],
    pts: 1000,
    emoji: '🔥',
    video: 'https://gif.ex.plo.re/wp-content/uploads/2026/04/segmind-intro-video-60f38e2c-b2bc-4be7-aa4a-f5a38362e31f-1.mp4',
    // ↑ Replace with your "Big Nose Big" hype video
  },
  {
    id: 'nbn',
    name: 'Nose Flip Nose',
    sequence: ['N', 'B', 'N'],
    pts: 1100,
    emoji: '🌀',
    video: 'https://gif.ex.plo.re/wp-content/uploads/2026/04/segmind-intro-video-06a96bf8-fd83-46c3-ba6c-6f66ba543679-1.mp4',
    // ↑ Replace with your "Nose Flip Nose" hype video
  },
  {
    id: 'bbn',
    name: 'Double Blast',
    sequence: ['B', 'B', 'N'],
    pts: 950,
    emoji: '💥',
    video: 'https://gif.ex.plo.re/wp-content/uploads/2026/04/segmind-intro-video-bf2ea9e4-3e35-42b8-9c81-3b6bb2d9e696-1.mp4',
    // ↑ Replace with your "Double Blast" hype video
  },
  {
    id: 'nbb',
    name: 'Nose Cannon',
    sequence: ['N', 'B', 'B'],
    pts: 1050,
    emoji: '🚀',
    video: 'https://gif.ex.plo.re/wp-content/uploads/2026/04/segmind-intro-video-60f38e2c-b2bc-4be7-aa4a-f5a38362e31f-1.mp4',
    // ↑ Replace with your "Nose Cannon" hype video
  },
  {
    id: 'bnn',
    name: 'Air Twister',
    sequence: ['B', 'N', 'N'],
    pts: 1150,
    emoji: '🌪️',
    video: 'https://gif.ex.plo.re/wp-content/uploads/2026/04/segmind-intro-video-06a96bf8-fd83-46c3-ba6c-6f66ba543679-1.mp4',
    // ↑ Replace with your "Air Twister" hype video
  },
  {
    id: 'nnb',
    name: 'Double Nose Bomb',
    sequence: ['N', 'N', 'B'],
    pts: 1200,
    emoji: '⚡',
    video: 'https://gif.ex.plo.re/wp-content/uploads/2026/04/segmind-intro-video-bf2ea9e4-3e35-42b8-9c81-3b6bb2d9e696-1.mp4',
    // ↑ Replace with your "Double Nose Bomb" hype video
  },

  // ── 4-KEY COMBOS ──────────────────────────────────────────

  {
    id: 'bnbn',
    name: 'Surf Infinity',
    sequence: ['B', 'N', 'B', 'N'],
    pts: 1500,
    emoji: '♾️',
    video: 'https://gif.ex.plo.re/wp-content/uploads/2026/04/segmind-intro-video-60f38e2c-b2bc-4be7-aa4a-f5a38362e31f-1.mp4',
    // ↑ Replace with your "Surf Infinity" hype video
  },
  {
    id: 'nbnb',
    name: 'Nose Storm',
    sequence: ['N', 'B', 'N', 'B'],
    pts: 1600,
    emoji: '⛈️',
    video: 'https://gif.ex.plo.re/wp-content/uploads/2026/04/segmind-intro-video-06a96bf8-fd83-46c3-ba6c-6f66ba543679-1.mp4',
    // ↑ Replace with your "Nose Storm" hype video
  },
  {
    id: 'bbnn',
    name: 'Rocket Spin',
    sequence: ['B', 'B', 'N', 'N'],
    pts: 1400,
    emoji: '🛸',
    video: 'https://gif.ex.plo.re/wp-content/uploads/2026/04/segmind-intro-video-bf2ea9e4-3e35-42b8-9c81-3b6bb2d9e696-1.mp4',
    // ↑ Replace with your "Rocket Spin" hype video
  },
  {
    id: 'nnbb',
    name: 'Gravity Crusher',
    sequence: ['N', 'N', 'B', 'B'],
    pts: 1450,
    emoji: '🪐',
    video: 'https://gif.ex.plo.re/wp-content/uploads/2026/04/segmind-intro-video-60f38e2c-b2bc-4be7-aa4a-f5a38362e31f-1.mp4',
    // ↑ Replace with your "Gravity Crusher" hype video
  },

  // ── 5-KEY LEGENDARY COMBOS ────────────────────────────────

  {
    id: 'bnbnb',
    name: '🏆 LEGEND WAVE',
    sequence: ['B', 'N', 'B', 'N', 'B'],
    pts: 2500,
    emoji: '👑',
    video: 'https://gif.ex.plo.re/wp-content/uploads/2026/04/segmind-intro-video-80da5868-7896-411d-808e-1e558994a152-1.mp4',
    // ↑ Replace with your LEGENDARY hype video — make it epic!
  },
  {
    id: 'nbnbn',
    name: '🏆 NOSE LEGEND',
    sequence: ['N', 'B', 'N', 'B', 'N'],
    pts: 2800,
    emoji: '🌊',
    video: 'https://gif.ex.plo.re/wp-content/uploads/2026/04/segmind-intro-video-80da5868-7896-411d-808e-1e558994a152-1.mp4',
    // ↑ Replace with your NOSE LEGEND hype video
  },
  {
    id: 'bbbnn',
    name: 'Triple Threat',
    sequence: ['B', 'B', 'B', 'N', 'N'],
    pts: 2000,
    emoji: '🎯',
    video: 'https://gif.ex.plo.re/wp-content/uploads/2026/04/segmind-intro-video-06a96bf8-fd83-46c3-ba6c-6f66ba543679-1.mp4',
    // ↑ Replace with your "Triple Threat" hype video
  },
  {
    id: 'nnbbb',
    name: 'Nose Avalanche',
    sequence: ['N', 'N', 'B', 'B', 'B'],
    pts: 2200,
    emoji: '🏔️',
    video: 'https://gif.ex.plo.re/wp-content/uploads/2026/04/segmind-intro-video-bf2ea9e4-3e35-42b8-9c81-3b6bb2d9e696-1.mp4',
    // ↑ Replace with your "Nose Avalanche" hype video
  },
];


// ─────────────────────────────────────────────────────────────
//  🎮  GAME INTRO VIDEO
//  Plays once when the player first enters the game.
//  Recommended length: 5–15 seconds.
// ─────────────────────────────────────────────────────────────
export const GAME_INTRO_VIDEO =
  'https://gif.ex.plo.re/wp-content/uploads/2026/04/segmind-intro-video-06a96bf8-fd83-46c3-ba6c-6f66ba543679-1.mp4';
//  ↑ Replace with your game intro video URL


// ─────────────────────────────────────────────────────────────
//  🗺️  TRACK INTRO VIDEOS  (one per area / track)
//  Plays when the player selects an area and the game begins.
//  Key = area ID (must match AreaConfig.id in AreaSelectScreen)
//  Recommended length: 3–8 seconds.
// ─────────────────────────────────────────────────────────────
export const TRACK_INTRO_VIDEOS: Record<string, string> = {
  italy:
    'https://gif.ex.plo.re/wp-content/uploads/2026/04/segmind-intro-video-bf2ea9e4-3e35-42b8-9c81-3b6bb2d9e696-1.mp4',
  //  ↑ Italy track intro — replace with your Italy intro video

  japan:        '', // TODO: add Japan track intro video URL
  alps:         '', // TODO: add Alps track intro video URL
  california:   '', // TODO: add California track intro video URL
  rainbow_road: '', // Rainbow Road — same pool as combo hype videos (no dedicated intro yet)
};


// ─────────────────────────────────────────────────────────────
//  🏆  TRACK COMPLETION / MILESTONE VIDEOS  (one per level)
//  Plays when the player completes a level / track milestone.
//  level 1 = Easy Trail, 2 = Mountain Path, 3 = Expert Run,
//  4 = Championship (final win).
//  Recommended length: 3–10 seconds.
// ─────────────────────────────────────────────────────────────
export const TRACK_COMPLETE_VIDEOS: Record<number, string> = {
  1: 'https://gif.ex.plo.re/wp-content/uploads/2026/04/segmind-intro-video-60f38e2c-b2bc-4be7-aa4a-f5a38362e31f-1.mp4',
  2: 'https://gif.ex.plo.re/wp-content/uploads/2026/04/segmind-intro-video-60f38e2c-b2bc-4be7-aa4a-f5a38362e31f-1.mp4',
  3: 'https://gif.ex.plo.re/wp-content/uploads/2026/04/segmind-intro-video-60f38e2c-b2bc-4be7-aa4a-f5a38362e31f-1.mp4',
  4: 'https://gif.ex.plo.re/wp-content/uploads/2026/04/segmind-intro-video-80da5868-7896-411d-808e-1e558994a152-1.mp4',
};


// ─────────────────────────────────────────────────────────────
//  💥  WIPEOUT / FALL VIDEOS
//  These play when the player crashes / wipes out.
//  One is chosen at random each time the player falls.
//  Recommended length: 2–4 seconds per video.
// ─────────────────────────────────────────────────────────────
export const WIPEOUT_VIDEOS: string[] = [
  'https://gif.ex.plo.re/wp-content/uploads/2026/04/segmind-intro-video-80da5868-7896-411d-808e-1e558994a152-1.mp4',
  // ↑ Wipeout video 1 — replace with a funny wipeout/crash reaction

  // Add more wipeout videos here:
  // 'https://your-cdn.com/wipeout-2.mp4',
  // 'https://your-cdn.com/wipeout-3.mp4',
];


// ─────────────────────────────────────────────────────────────
//  ⚡  LEGACY: COMBO_VIDEOS (kept for backward compatibility)
//  The game now uses COMBO_CATALOG above for named combos.
//  This array is used as a fallback for unrecognized sequences.
// ─────────────────────────────────────────────────────────────
export const COMBO_VIDEOS: string[] = [
  'https://gif.ex.plo.re/wp-content/uploads/2026/04/segmind-intro-video-06a96bf8-fd83-46c3-ba6c-6f66ba543679-1.mp4',
  'https://gif.ex.plo.re/wp-content/uploads/2026/04/segmind-intro-video-bf2ea9e4-3e35-42b8-9c81-3b6bb2d9e696-1.mp4',
  'https://gif.ex.plo.re/wp-content/uploads/2026/04/segmind-intro-video-60f38e2c-b2bc-4be7-aa4a-f5a38362e31f-1.mp4',
];


// ─────────────────────────────────────────────────────────────
//  🎲  HELPERS
// ─────────────────────────────────────────────────────────────

/** Pick a random video URL from an array (skips empty strings). */
export function pickRandom(videos: string[]): string | null {
  const valid = videos.filter(v => v && v.trim().length > 0);
  if (valid.length === 0) return null;
  return valid[Math.floor(Math.random() * valid.length)];
}

/**
 * Match a key sequence against the COMBO_CATALOG.
 * Returns the best (longest) matching combo, or null.
 * Sequences are matched as a suffix of the provided taps array.
 */
export function matchCombo(taps: string[]): ComboDefinition | null {
  // Sort by sequence length descending so longest match wins
  const sorted = [...COMBO_CATALOG].sort((a, b) => b.sequence.length - a.sequence.length);
  for (const combo of sorted) {
    const len = combo.sequence.length;
    if (taps.length < len) continue;
    const tail = taps.slice(taps.length - len);
    if (tail.every((k, i) => k === combo.sequence[i])) {
      return combo;
    }
  }
  return null;
}
